<template>
  <div class="fin-dash">
    <header class="fin-dash__head">
      <div class="fin-dash__head-text">
        <h1 class="fin-dash__title">재무제표 요약</h1>
        <p class="fin-dash__sub">
          분기 말 기준 재무상태·손익·현금흐름 핵심만 모았습니다. 단위는
          억원입니다.
        </p>
      </div>
      <div class="fin-dash__head-actions">
        <el-date-picker
          v-model="periodMonth"
          type="month"
          size="small"
          placeholder="기준 말"
          value-format="yyyy-MM"
          format="yyyy년 M월"
          class="fin-dash__month"
          @change="onPeriodChange"
        />
        <el-tooltip content="무작위로 수치를 다시 맞춥니다" placement="bottom">
          <el-button
            type="default"
            size="small"
            icon="el-icon-refresh"
            circle
            class="fin-dash__refresh"
            @click="onRefresh"
          />
        </el-tooltip>
      </div>
    </header>

    <section class="fin-dash__kpis fin-dash__kpis--six">
      <el-card
        v-for="item in kpis"
        :key="item.key"
        class="fin-dash__kpi-card"
        shadow="hover"
      >
        <p class="fin-dash__kpi-label">{{ item.label }}</p>
        <p class="fin-dash__kpi-value">{{ item.value }}</p>
        <p class="fin-dash__kpi-meta">{{ item.meta }}</p>
      </el-card>
    </section>

    <el-row :gutter="20" class="fin-dash__row">
      <el-col :xs="24" :lg="16">
        <el-card class="fin-dash__chart-card" shadow="never">
          <div slot="header" class="fin-dash__card-head">
            <span>분기별 자산·부채·자본 (최근 4분기)</span>
          </div>
          <apexchart
            :key="'bs-bar-' + chartRevision"
            type="bar"
            height="320"
            :options="quarterBarOptions"
            :series="quarterBarSeries"
          />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="8">
        <el-card class="fin-dash__chart-card" shadow="never">
          <div slot="header" class="fin-dash__card-head">
            <span>자산 구성 (유동 / 비유동)</span>
          </div>
          <apexchart
            :key="'asset-donut-' + chartRevision"
            type="donut"
            height="320"
            :options="assetDonutOptions"
            :series="assetDonutSeries"
          />
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="fin-dash__row">
      <el-col :xs="24" :md="8">
        <el-card class="fin-dash__cf-card" shadow="never">
          <p class="fin-dash__cf-label">영업활동 CF</p>
          <p class="fin-dash__cf-value">{{ cfOps }}</p>
          <p class="fin-dash__cf-hint">감가 등 비현금 조정 반영 목업</p>
        </el-card>
      </el-col>
      <el-col :xs="24" :md="8">
        <el-card class="fin-dash__cf-card" shadow="never">
          <p class="fin-dash__cf-label">투자활동 CF</p>
          <p class="fin-dash__cf-value">{{ cfInv }}</p>
          <p class="fin-dash__cf-hint">설비·SW 투자 목업</p>
        </el-card>
      </el-col>
      <el-col :xs="24" :md="8">
        <el-card class="fin-dash__cf-card" shadow="never">
          <p class="fin-dash__cf-label">재무활동 CF</p>
          <p class="fin-dash__cf-value">{{ cfFin }}</p>
          <p class="fin-dash__cf-hint">차입 상환·배당 목업</p>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="fin-dash__row">
      <el-col :xs="24" :lg="12">
        <el-card class="fin-dash__chart-card" shadow="never">
          <div slot="header" class="fin-dash__card-head">
            <span>손익 핵심 (분기 누적)</span>
          </div>
          <el-table
            :data="plMiniRows"
            stripe
            size="small"
            class="fin-dash__table"
          >
            <el-table-column prop="line" label="구분" min-width="140" />
            <el-table-column
              prop="amt"
              label="금액"
              width="120"
              align="right"
            />
            <el-table-column prop="note" label="비고" min-width="120" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card class="fin-dash__chart-card" shadow="never">
          <div slot="header" class="fin-dash__card-head">
            <span>주요 잔액 (기준 말)</span>
          </div>
          <el-table
            :data="majorRows"
            stripe
            size="small"
            class="fin-dash__table"
          >
            <el-table-column prop="name" label="과목" min-width="140" />
            <el-table-column
              prop="amt"
              label="금액"
              width="110"
              align="right"
            />
            <el-table-column
              prop="pct"
              label="자산 대비"
              width="100"
              align="right"
            />
          </el-table>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
const PRIMARY = "#254aa5";
const PRIMARY_LIGHT = "#4a90e2";
const MUTED = "#8b95a5";

const QUARTERS = ["2025 Q2", "2025 Q3", "2025 Q4", "2026 Q1"];

function buildQuarterBars(seed) {
  const baseA = 82 + (seed % 5);
  const assets = QUARTERS.map((_, i) =>
    Number((baseA + i * 1.4 + ((seed + i) % 7) / 10).toFixed(1))
  );
  const liab = assets.map((a, i) =>
    Number((a * 0.58 + ((seed + i * 2) % 5) / 10).toFixed(1))
  );
  const eq = assets.map((a, i) => Number((a - liab[i]).toFixed(1)));
  return { assets, liab, eq };
}

function buildAssetDonut(seed) {
  const a = 42 + (seed % 8);
  const b = 100 - a;
  return [a, b];
}

export default {
  name: "FinanceStatementsDashboard",
  data() {
    const seed = 73;
    const q = buildQuarterBars(seed);
    const donut = buildAssetDonut(seed);
    return {
      chartRevision: 0,
      periodMonth: "2026-04",
      cfOps: "+2.14억",
      cfInv: "−0.88억",
      cfFin: "−0.32억",
      kpis: [
        {
          key: "ast",
          label: "자산 총계",
          value: "₩ 88.2억",
          meta: "전분기 대비 +2.1%",
        },
        {
          key: "lia",
          label: "부채 총계",
          value: "₩ 51.4억",
          meta: "전분기 대비 +1.2%",
        },
        {
          key: "eq",
          label: "자본 총계",
          value: "₩ 36.8억",
          meta: "전분기 대비 +3.4%",
        },
        {
          key: "dr",
          label: "부채비율",
          value: "139.7%",
          meta: "부채 ÷ 자본",
        },
        {
          key: "cr",
          label: "유동비율",
          value: "182%",
          meta: "유동자산 ÷ 유동부채",
        },
        {
          key: "nim",
          label: "당기순이익",
          value: "₩ 0.79억",
          meta: "분기 누적 (목업)",
        },
      ],
      quarterBarSeries: [
        { name: "자산", data: q.assets },
        { name: "부채", data: q.liab },
        { name: "자본", data: q.eq },
      ],
      quarterBarOptions: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          fontFamily: "Pretendard, sans-serif",
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "58%",
            borderRadius: 4,
          },
        },
        colors: [PRIMARY, "#e6a23c", "#67c23a"],
        dataLabels: { enabled: false },
        xaxis: {
          categories: QUARTERS,
          labels: { style: { colors: MUTED, fontSize: "11px" } },
        },
        yaxis: {
          labels: {
            style: { colors: MUTED },
            formatter: (v) => `${v}`,
          },
        },
        grid: { borderColor: "#eef0f3" },
        legend: {
          position: "top",
          horizontalAlign: "right",
          fontSize: "12px",
          labels: { colors: "#505258" },
        },
        tooltip: {
          theme: "light",
          fillSeriesColor: false,
          y: { formatter: (v) => `${v}억` },
        },
      },
      assetDonutSeries: donut,
      assetDonutOptions: {
        chart: { fontFamily: "Pretendard, sans-serif" },
        labels: ["유동자산", "비유동자산"],
        colors: [PRIMARY, PRIMARY_LIGHT],
        legend: {
          position: "bottom",
          fontSize: "12px",
          labels: { colors: "#505258" },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "68%",
              labels: {
                show: true,
                name: { show: true },
                value: {
                  show: true,
                  formatter: (val) =>
                    val != null && val !== "" ? `${val}%` : "",
                },
                total: { show: false },
              },
            },
          },
        },
        dataLabels: { enabled: false },
        tooltip: {
          theme: "light",
          fillSeriesColor: false,
          y: { formatter: (v) => `${v}%` },
        },
      },
      plMiniRows: [
        { line: "매출액", amt: "31.2억", note: "QoQ +5.8%" },
        { line: "영업이익", amt: "1.08억", note: "영업이익률 3.5%" },
        { line: "당기순이익", amt: "0.79억", note: "순이익률 2.5%" },
      ],
      majorRows: [
        { name: "현금및현금성자산", amt: "12.4억", pct: "14.1%" },
        { name: "매출채권", amt: "18.6억", pct: "21.1%" },
        { name: "재고자산", amt: "9.8억", pct: "11.1%" },
        { name: "유형자산", amt: "24.2억", pct: "27.4%" },
        { name: "차입금(유동+비유동)", amt: "28.0억", pct: "—" },
      ],
    };
  },
  methods: {
    onPeriodChange() {
      this.regenerateFromSeed(
        this.hashPeriod(this.periodMonth) + (Date.now() % 997)
      );
    },
    onRefresh() {
      this.regenerateFromSeed(Date.now() % 10000);
      this.$message.success("재무제표 요약 데이터를 갱신했습니다.");
    },
    hashPeriod(ym) {
      if (!ym || typeof ym !== "string") return 0;
      return ym.split("-").reduce((a, x) => a + parseInt(x, 10), 0);
    },
    fmtEok(n) {
      return `${n.toFixed(1)}억`;
    },
    regenerateFromSeed(seed) {
      const q = buildQuarterBars(seed);
      const donut = buildAssetDonut(seed);
      const lastA = q.assets[q.assets.length - 1];
      const lastL = q.liab[q.liab.length - 1];
      const lastE = q.eq[q.eq.length - 1];
      const prevE = q.eq[q.eq.length - 2] || lastE * 0.96;
      const debtRatio = lastE > 0 ? ((lastL / lastE) * 100).toFixed(1) : "—";
      const currentRatio = `${165 + (seed % 25)}%`;

      this.quarterBarSeries = [
        { name: "자산", data: q.assets },
        { name: "부채", data: q.liab },
        { name: "자본", data: q.eq },
      ];
      this.quarterBarOptions = {
        ...this.quarterBarOptions,
        xaxis: {
          ...this.quarterBarOptions.xaxis,
          categories: QUARTERS,
        },
      };
      this.assetDonutSeries = donut;

      const net = Number((lastE * 0.021 + (seed % 10) / 100).toFixed(2));
      const sales = Number((lastA * 0.35 + (seed % 8) / 10).toFixed(1));
      const op = Number((sales * 0.035 + (seed % 5) / 20).toFixed(2));

      this.kpis = [
        {
          key: "ast",
          label: "자산 총계",
          value: `₩ ${this.fmtEok(lastA)}`,
          meta: "최근 분기 기준",
        },
        {
          key: "lia",
          label: "부채 총계",
          value: `₩ ${this.fmtEok(lastL)}`,
          meta: "최근 분기 기준",
        },
        {
          key: "eq",
          label: "자본 총계",
          value: `₩ ${this.fmtEok(lastE)}`,
          meta: `전분기 대비 ${(((lastE - prevE) / prevE) * 100 || 0).toFixed(
            1
          )}%`,
        },
        {
          key: "dr",
          label: "부채비율",
          value: `${debtRatio}%`,
          meta: "부채 ÷ 자본",
        },
        {
          key: "cr",
          label: "유동비율",
          value: currentRatio,
          meta: "유동자산 ÷ 유동부채",
        },
        {
          key: "nim",
          label: "당기순이익",
          value: `₩ ${this.fmtEok(net)}`,
          meta: "분기 누적 (목업)",
        },
      ];

      this.cfOps = `+${(1.8 + (seed % 15) / 10).toFixed(2)}억`;
      this.cfInv = `−${(0.5 + (seed % 10) / 10).toFixed(2)}억`;
      const finv = (0.2 + (seed % 8) / 10).toFixed(2);
      this.cfFin = `−${finv}억`;

      this.plMiniRows = [
        { line: "매출액", amt: `${sales}억`, note: "QoQ 샘플" },
        {
          line: "영업이익",
          amt: `${op}억`,
          note: `영업이익률 ${((op / sales) * 100).toFixed(1)}%`,
        },
        { line: "당기순이익", amt: `${net}억`, note: "순이익률 목업" },
      ];

      const cash = Number((lastA * 0.14 + (seed % 5) / 10).toFixed(1));
      const ar = Number((lastA * 0.21 + (seed % 7) / 10).toFixed(1));
      const inv = Number((lastA * 0.11 + (seed % 4) / 10).toFixed(1));
      const ppe = Number((lastA * 0.27 + (seed % 6) / 10).toFixed(1));
      const debt = Number((lastL * 0.54 + (seed % 5) / 10).toFixed(1));
      this.majorRows = [
        {
          name: "현금및현금성자산",
          amt: `${cash}억`,
          pct: `${((cash / lastA) * 100).toFixed(1)}%`,
        },
        {
          name: "매출채권",
          amt: `${ar}억`,
          pct: `${((ar / lastA) * 100).toFixed(1)}%`,
        },
        {
          name: "재고자산",
          amt: `${inv}억`,
          pct: `${((inv / lastA) * 100).toFixed(1)}%`,
        },
        {
          name: "유형자산",
          amt: `${ppe}억`,
          pct: `${((ppe / lastA) * 100).toFixed(1)}%`,
        },
        {
          name: "차입금(유동+비유동)",
          amt: `${debt}억`,
          pct: "—",
        },
      ];

      this.chartRevision += 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.fin-dash {
  width: 100%;
  padding: 24px 32px 48px;
  box-sizing: border-box;
  background: linear-gradient(180deg, #f4f6f8 0%, #fff 28%);
}

.fin-dash__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 18px;
  border-bottom: 1px solid #ebeef5;
}

.fin-dash__head-text {
  min-width: 0;
}

.fin-dash__title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  letter-spacing: -0.03em;
}

.fin-dash__sub {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #909399;
}

.fin-dash__head-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.fin-dash__month {
  width: 168px;
}

.fin-dash__kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 20px;
}

.fin-dash__kpis--six {
  grid-template-columns: repeat(3, 1fr);
}

@media (max-width: 992px) {
  .fin-dash__kpis--six {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .fin-dash__kpis--six {
    grid-template-columns: 1fr;
  }
}

.fin-dash__kpi-card {
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.fin-dash__kpi-label {
  margin: 0 0 6px;
  font-size: 12px;
  color: #909399;
}

.fin-dash__kpi-value {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 700;
  color: #303133;
  letter-spacing: -0.02em;
}

.fin-dash__kpi-meta {
  margin: 0;
  font-size: 11px;
  color: #606266;
  line-height: 1.35;
}

.fin-dash__row {
  margin-bottom: 20px !important;
}

@media (max-width: 1199px) {
  .fin-dash__row > .el-col:not(:last-child) {
    margin-bottom: 20px;
  }
}

.fin-dash__chart-card {
  border-radius: 8px;
  border: 1px solid #ebeef5;
  margin-bottom: 0;
}

.fin-dash__card-head {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.fin-dash__table {
  width: 100%;
}

.fin-dash__cf-card {
  border-radius: 8px;
  border: 1px solid #ebeef5;
  margin-bottom: 0;
}

.fin-dash__cf-card ::v-deep .el-card__body {
  padding: 18px 20px;
}

.fin-dash__cf-label {
  margin: 0 0 8px;
  font-size: 12px;
  color: #909399;
}

.fin-dash__cf-value {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  letter-spacing: -0.02em;
}

.fin-dash__cf-hint {
  margin: 0;
  font-size: 11px;
  color: #c0c4cc;
  line-height: 1.4;
}
</style>
